import {faSpinnerGap} from '@awesome.me/kit-d4c1b300b9/icons/kit/custom';
import type {HTMLChakraProps} from '@chakra-ui/react';
import {forwardRef, useStyleConfig, keyframes} from '@chakra-ui/react';
import type {ThemingProps} from '@chakra-ui/styled-system';
import {omitThemingProps} from '@chakra-ui/styled-system';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {ChakraDiv, ChakraSpan} from '@linx-security/ui/utils/chakra-elements';
import cx from 'clsx';

const spin = keyframes({
  '0%': {
    transform: `rotate(0deg)`,
  },
  '100%': {
    transform: `rotate(360deg)`,
  },
});

export interface SpinnerIconOptions {
  /**
   * The color of the spinner
   */
  color?: string;
  /**
   * The speed of the spinner.
   * @default "1s"
   * @example
   * ```jsx
   * <Spinner speed="0.2s"/>
   * ```
   */
  speed?: string;
  /**
   * For accessibility, it is important to add a fallback loading text.
   * This text will be visible to screen readers.
   * @default "Loading..."
   */
  label?: string;
}

export interface SpinnerIconProps
  extends Omit<HTMLChakraProps<'div'>, keyof SpinnerIconOptions>,
    SpinnerIconOptions,
    ThemingProps<'Spinner'> {}

export const SpinnerIcon = forwardRef<SpinnerIconProps, 'div'>((props, ref) => {
  const styles = useStyleConfig(`Spinner`, props);
  const {label = `Loading...`, speed = `1s`, className, ...rest} = omitThemingProps(props);

  const _className = cx(`chakra-spinner`, className);
  const spinnerStyles = {
    animation: `${spin} ${speed} linear infinite`,
    ...styles,
  };

  return (
    <ChakraDiv ref={ref} __css={spinnerStyles} className={_className} {...rest}>
      <FontAwesomeIcon icon={faSpinnerGap} style={{width: `100%`, height: `100%`, display: `block`}} />
      {label && <ChakraSpan srOnly>{label}</ChakraSpan>}
    </ChakraDiv>
  );
});
